import React, { useState, useMemo } from 'react';
import Select from 'react-select';
import TradingViewChartIcon from './TradingViewChartIcon';
import './TrendCombinationView.css';
import { ensureArray } from './config';

const trendFields = [
  'priceXEma12Trend',
  'priceXEma21Trend',
  'priceXEma36Trend',  
  'priceXEma50Trend',
  'priceXEma100Trend',
  'priceXEma200Trend',
  'appEma21XEma36Trend',
  'appEma36XEma50Trend',
  'appEma36XEma200Trend',
  'appEma50XEma200Trend',
  'appEma100XEma200Trend',
  'appEma50XEma100Trend',
  'priceXVWAP24Trend',
  'priceXVWAPWeeklyTrend'
];

const TrendCombinationView = ({ data }) => {
  const safeData = ensureArray(data);
  const [filters, setFilters] = useState({ symbols: [], timeFrames: [], trendType: '' });
  const [columnFilters, setColumnFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'symbol', direction: 'asc' });
  const [expanded, setExpanded] = useState(false);

  const symbols = [...new Set(safeData.map(d => d.symbol))];
  const timeFrames = [...new Set(safeData.map(d => d.timeFrame))];
  const symbolOptions = symbols.sort().map(value => ({ value, label:value }));
  const timeFrameOptions = timeFrames.sort((a,b) => a-b).map(value => ({ value:String(value), label:`${value} min` }));
  const selectedSummary = (values, suffix = '') => values.length ? `${values.slice(0, 3).map(value => `${value}${suffix}`).join(', ')}${values.length > 3 ? ` +${values.length - 3}` : ''}` : 'All';
  let lastSymbol = null;
  let symbolBgToggle = false;

  const filtered = useMemo(() => {
    return safeData.filter(item => {
      if (filters.symbols.length && !filters.symbols.includes(item.symbol)) return false;
      if (filters.timeFrames.length && !filters.timeFrames.includes(String(item.timeFrame))) return false;

     // All BULL or All BEAR logic (ignore empty/null fields)
     if (filters.trendType === 'ALL_BULL') {
       // only look at fields that actually have a value
       const nonEmpty = trendFields.filter(f => item[f] != null && item[f] !== '');
       // must have at least one and all non-empty must be BULL
       if (nonEmpty.length === 0 || !nonEmpty.every(f => item[f] === 'BULL')) {
         return false;
       }
     } else if (filters.trendType === 'ALL_BEAR') {
       const nonEmpty = trendFields.filter(f => item[f] != null && item[f] !== '');
       if (nonEmpty.length === 0 || !nonEmpty.every(f => item[f] === 'BEAR')) {
         return false;
       }
     }

      // Column specific filtering
      for (let field of trendFields) {
        if (columnFilters[field] && item[field] !== columnFilters[field]) return false;
      }

      return true;
    });
  }, [safeData, filters, columnFilters]);

  const sorted = useMemo(() => {
    const sortedList = [...filtered];
    const { key, direction } = sortConfig;
    return sortedList.sort((a, b) => {
      let valA = a[key];
      let valB = b[key];
      if (valA == null || valA === '') valA = direction === 'asc' ? 'ZZZ' : '';
      if (valB == null || valB === '') valB = direction === 'asc' ? 'ZZZ' : '';
      if (key === 'timeFrame') {
        valA = parseInt(valA);
        valB = parseInt(valB);
      }
      return direction === 'asc'
        ? valA > valB ? 1 : valA < valB ? -1 : 0
        : valA < valB ? 1 : valA > valB ? -1 : 0;
    });
  }, [filtered, sortConfig]);

  const displayData = expanded ? sorted : sorted.slice(0, 10);

  const handleSort = (key) => {
    setSortConfig(prev =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  };

  const getTrendClass = (value) => {
    if (value === 'BULL') return 'trend-bull';
    if (value === 'BEAR') return 'trend-bear';
    return '';
  };

  return (
    <div className="trend-container">
      <h3 className="trend-view-title">Trend Combination View</h3>

      <div className="filters">
        <div className="trend-multi-filter"><Select isMulti classNamePrefix="trend-select" options={symbolOptions} value={symbolOptions.filter(option => filters.symbols.includes(option.value))} onChange={options => setFilters({ ...filters, symbols:(options || []).map(option => option.value) })} placeholder="Symbols"/><small>{selectedSummary(filters.symbols)}</small></div>

        <div className="trend-multi-filter"><Select isMulti classNamePrefix="trend-select" options={timeFrameOptions} value={timeFrameOptions.filter(option => filters.timeFrames.includes(option.value))} onChange={options => setFilters({ ...filters, timeFrames:(options || []).map(option => option.value) })} placeholder="Time Frames"/><small>{selectedSummary(filters.timeFrames, 'm')}</small></div>

        <select value={filters.trendType} onChange={e => setFilters({ ...filters, trendType: e.target.value })}>
          <option value="">Select Trend Type</option>
          <option value="ALL_BULL">All BULL</option>
          <option value="ALL_BEAR">All BEAR</option>
        </select>

        {filtered.length > 10 && (
          <button className="expand-button" onClick={() => setExpanded(!expanded)}>
            {expanded ? 'Collapse' : 'Expand'}
          </button>
        )}

        <button className="trend-clear-btn" onClick={() => {
          setFilters({ symbols: [], timeFrames: [], trendType: '' });
          setColumnFilters({});
        }}>
          <span className="trend-clear-icon" aria-hidden="true"/>Clear Filters
        </button>
      </div>

      <div className="trend-table-wrapper">
        <table className="trend-table">
          <thead>
            <tr>
              <th>#</th>
              <th onClick={() => handleSort('symbol')}>Symbol</th>
              <th onClick={() => handleSort('timeFrame')}>Time Frame</th>
              <th>Chart</th>
              {trendFields.map(field => (
                <th key={field} onClick={() => handleSort(field)}>
                  {field} {sortConfig.key === field ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '⬍'}
                </th>
              ))}
            </tr>
            <tr>
              <th></th>
              <th></th>
              <th></th>
              <th></th>
              {trendFields.map(field => (
                <th key={`filter-${field}`}>
                  <select
                    value={columnFilters[field] || ''}
                    onChange={e => setColumnFilters({ ...columnFilters, [field]: e.target.value })}
                  >
                    <option value="">All</option>
                    <option value="BULL">BULL</option>
                    <option value="BEAR">BEAR</option>
                  </select>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
          {displayData.map((item, idx) => {
             // whenever symbol changes, flip our toggle
             if (item.symbol !== lastSymbol) {
               symbolBgToggle = !symbolBgToggle;
               lastSymbol = item.symbol;
             }
             // pick one of two classes
             const rowClass = symbolBgToggle 
               ? 'symbol-bg-light' 
               : 'symbol-bg-dark';

             return (
               <tr key={idx} className={rowClass}>
                <td>{idx + 1}</td>
                 <td>{item.symbol}</td>
                 <td>{item.timeFrame} min</td>
                <td>
                  <TradingViewChartIcon
                    symbol={item.symbol}
                    timeFrame={item.timeFrame}
                    exchange={item.exchange}
                  />
                </td>
                 {trendFields.map(field => (
                   <td key={field} className={getTrendClass(item[field])}>
                     {item[field] || '-'}
                   </td>
                 ))}
               </tr>
             );
           })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TrendCombinationView;
